/**
 * ESCHER FALLS — ステージ選択画面。METAGAME-SPEC §3。
 *
 * `#ui` の中に自前の DOM を組み、自前の `<style>` を注入する自己完結モジュール。
 * `hud.ts` には一切依存しない（`createHud` が `#ui` を `replaceChildren` するので、
 * **必ずその後に生成すること**）。
 *
 * 表示の原則:
 * - **どの視点で解けるかは絶対に出さない。** ここに出すと解答表になる。
 * - 未解放のエクストラは「存在だけ見せて中身は伏せる」。
 *   存在が見えていないと、解放されたときに報酬にならない（METAGAME-SPEC §3）。
 */

import type { Level } from '../core/types';
import type { RecordsData } from '../game/records';
import {
  buildStageList,
  hasCompletionTitle,
  mainProgress,
  mainTotalBestTime,
  rankOf,
  type StageEntry,
} from '../game/progression';
import { formatTime } from '../game/timer';
import { stageNumber } from './kanji';
import { t } from './i18n';
import { levelName } from './i18nLevels';

const STYLE_ID = 'ef-stage-select-style';

const CSS = `
/* ブラウザ既定の <p> マージンを潰す（残ると縦がスカスカになる）。 */
.ef-ss p { margin: 0; }

/* 視点インジケータの真下に置く。右上に横並びにすると、狭い画面で
   左上のステージ名に食い込む（実際に重なった）。 */
.ef-ss-open {
  position: absolute;
  top: calc(var(--hud-gap, 16px) + var(--safe-top, 0px) + 72px);
  right: calc(var(--hud-gap, 16px) + var(--safe-right, 0px));
  font-size: 12px;
  padding: 6px 12px;
}

.ef-ss {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  overflow-y: auto;
  padding: calc(var(--safe-top, 0px) + 32px) 20px calc(var(--safe-bottom, 0px) + 32px);
  background: var(--paper, #f2efe6);
  pointer-events: auto;
}
.ef-ss.is-on { display: flex; animation: ef-ss-in 620ms ease-out; }

/* 淡く浮かび上がる。祝いの画面が溶暗するのと重なって、溶暗つなぎになる。
   起動時にも効くが、そこでも「紙が現れる」ように見えて具合がよい。 */
@keyframes ef-ss-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* セーフエリアぶんを紙色で覆う。
   padding-top はスクロール**内容**の頭にしか効かないので、下へ送ると
   札が時計とダイナミックアイランドの下をくぐって字が重なる（実機で確認）。
   覆いは常に画面の上端に貼りついていないといけないので fixed。 */
.ef-ss::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--safe-top, 0px);
  background: var(--paper, #f2efe6);
  pointer-events: none;
}

.ef-ss-head { text-align: center; }
.ef-ss-title {
  font-family: var(--font-serif, serif);
  font-size: 28px;
  letter-spacing: 0.4em;
  text-indent: 0.4em;
  color: var(--ink, #2b2823);
}
.ef-ss-sub {
  margin-top: 6px;
  font-size: 13px;
  letter-spacing: 0.16em;
  color: var(--ink-soft, #6b665c);
  font-variant-numeric: tabular-nums;
}

/* 踏破の称号。**祝いの画面は閉じれば消えるが、これは消えない。**
   「やり遂げた」証拠として置いてある以上、毎回ここで目に入る必要がある。 */
.ef-ss-award {
  margin-top: 10px;
  padding: 8px 20px;
  border: 1px solid var(--water, #4a8c8a);
  font-family: var(--font-serif, serif);
  font-size: 15px;
  letter-spacing: 0.26em;
  text-indent: 0.26em;
  color: var(--water, #4a8c8a);
}
.ef-ss-award[hidden] { display: none; }

.ef-ss-group {
  width: 100%;
  max-width: 780px;
}
.ef-ss-group[hidden] { display: none; }
.ef-ss-group-title {
  font-size: 12px;
  letter-spacing: 0.3em;
  color: var(--ink-soft, #6b665c);
  border-bottom: 1px solid var(--ink-soft, #6b665c);
  padding-bottom: 5px;
  margin-bottom: 12px;
}

.ef-ss-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 10px;
}

.ef-ss-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 14px 8px 11px;
  background: transparent;
  border: 1px solid var(--ink, #2b2823);
  color: var(--ink, #2b2823);
  font-family: var(--font-serif, serif);
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease;
}
/* hover はマウス環境だけ。タッチだと選んだ札が浮いたまま張り付く。 */
@media (hover: hover) and (pointer: fine) {
  .ef-ss-card:hover:not(:disabled) { background: var(--paper-shade, #e6e2d6); transform: translateY(-2px); }
}
.ef-ss-card:active:not(:disabled) { background: var(--paper-shade, #e6e2d6); }
.ef-ss-card:disabled { cursor: default; border-color: var(--ink-soft, #6b665c); opacity: 0.32; }

.ef-ss-no { font-size: 26px; letter-spacing: 0.1em; line-height: 1.1; }
.ef-ss-name { font-size: 13px; letter-spacing: 0.08em; text-align: center; }
.ef-ss-time {
  font-size: 11px;
  letter-spacing: 0.06em;
  font-variant-numeric: tabular-nums;
  color: var(--ink-soft, #6b665c);
}
/* クリア済みの印。水の色だけが彩度を持つ、という原則に合わせる */
.ef-ss-card.is-cleared { border-color: var(--water, #4a8c8a); }
.ef-ss-card.is-cleared .ef-ss-time { color: var(--water, #4a8c8a); }

.ef-ss-card.is-trial { border-color: var(--water, #4a8c8a); }
.ef-ss-card.is-trial .ef-ss-time { color: var(--water, #4a8c8a); }
.ef-ss-demo {
  width: 100%; max-width: 380px;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 14px 16px; margin-bottom: 4px;
  border: 1px solid var(--water, #4a8c8a);
}
.ef-ss-demo[hidden] { display: none; }
.ef-ss-demo-note { margin: 0; font-size: 13px; letter-spacing: 0.08em; text-align: center;
  color: var(--ink, #2b2823); text-wrap: balance; }
.ef-ss-demo-link { text-decoration: none; }

.ef-ss-daily {
  width: 100%; max-width: 380px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 14px 16px; margin-bottom: 4px;
  border: 1px solid var(--water, #4a8c8a);
}
.ef-ss-daily[hidden] { display: none; }
.ef-ss-daily-title { margin: 0; font-size: 15px; letter-spacing: 0.22em; color: var(--ink, #2b2823); }
.ef-ss-daily-note { margin: 0; font-size: 12px; letter-spacing: 0.1em; color: var(--ink-soft, #6b665c);
  text-align: center; text-wrap: balance; }
.ef-ss-daily-note[hidden], .ef-ss-daily-play[hidden] { display: none; }

.ef-ss-locked-note {
  font-size: 12px;
  letter-spacing: 0.14em;
  color: var(--ink-soft, #6b665c);
}

.ef-ss-foot { display: flex; gap: 12px; margin-top: 4px; }

/* 周回の切り替え。**解放されるまで出さない。**
   出しておいて押せないより、無いほうが画面が静かになる。 */
/* 盤面の「≡」と同じ位置・同じ見た目にする。**同じ物は同じ場所に。** */
.ef-ss-set {
  position: absolute;
  top: calc(var(--safe-top, 0px) + 16px);
  left: calc(var(--safe-left, 0px) + 16px);
  font-size: 15px;
  line-height: 1;
  padding: 8px 12px;
  letter-spacing: 0;
}
.ef-ss-store {
  margin-top: 14px;
  font-size: 13px;
  letter-spacing: 0.18em;
  padding: 8px 22px;
  border-color: var(--water, #4a8c8a);
  color: var(--water, #4a8c8a);
}
.ef-ss-store[hidden] { display: none; }
.ef-ss-loops { display: flex; gap: 8px; margin-top: 12px; }
.ef-ss-loops[hidden] { display: none; }
.ef-ss-loop { font-size: 12px; padding: 6px 14px; }
.ef-ss-loop.is-on {
  color: var(--paper, #f2efe6);
  background: var(--ink, #2b2823);
  border-color: var(--ink, #2b2823);
}
.ef-ss-loop-note {
  margin-top: 8px;
  font-size: 11px;
  letter-spacing: 0.12em;
  color: var(--ink-soft, #6b665c);
}
.ef-ss-loop-note[hidden] { display: none; }

@media (max-width: 480px) {
  .ef-ss-title { font-size: 22px; }
  .ef-ss-grid { grid-template-columns: repeat(auto-fill, minmax(104px, 1fr)); }
  .ef-ss-no { font-size: 21px; }
  .ef-ss-name { font-size: 11px; }
}
`;

let styleRefCount = 0;

function injectStyles(): void {
  styleRefCount += 1;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function releaseStyles(): void {
  styleRefCount = Math.max(0, styleRefCount - 1);
  if (styleRefCount > 0) return;
  document.getElementById(STYLE_ID)?.remove();
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** 今日の一面（日替わり）の見え方。main が `game/daily.ts` の結果から組む。 */
export interface DailyView {
  /** 取りに行っている／遊べる／通信できず開けない。 */
  readonly status: 'loading' | 'ready' | 'offline';
  /** 表示用の日付（「10月9日」など、言語に合わせて main が整える）。 */
  readonly dateLabel: string;
  /** 今日の面を通したときのいちばん良いタイム。未通水なら null。 */
  readonly bestTimeMs: number | null;
  /** これまでの一面（新しい順）。 */
  readonly archive: readonly ArchiveDayView[];
  /** 先の一面（近い順）。エクストラを買った人にだけ main が渡す。無料なら空。 */
  readonly upcoming: readonly ArchiveDayView[];
  /** 開いていない日が残っているか（＝購入の案内を出すか）。 */
  readonly archiveHasLocked: boolean;
}

/** これまでの一面の1日ぶん。 */
export interface ArchiveDayView {
  readonly date: string;
  /** 表示用の日付（「10月8日」など）。 */
  readonly label: string;
  readonly open: boolean;
  readonly bestTimeMs: number | null;
}

export interface StageSelectCallbacks {
  /**
   * 描画に使う最新の状態を返す。**開くたびに呼ぶ。**
   * 記録はクリアのたびに変わるので、生成時に受け取って抱え込んではいけない。
   */
  getState(): {
    levels: readonly Level[];
    records: RecordsData;
    unlockAll?: boolean;
    /** いま何周目を見ているか。 */
    loop: number;
    /** 体験版の上限（`game/demo.ts`）。アプリでは undefined。 */
    demoLimit?: number;
    /** 今日の一面の様子。undefined なら枠ごと出さない。 */
    daily?: DailyView;
  };
  /** 今日の一面が押された。 */
  onPlayDaily?(): void;
  /** これまでの一面のある日が押された。 */
  onPlayArchive?(date: string): void;
  /** 周回を切り替えた。main が以後の面読み込みに反映する。 */
  onLoopChange(loop: number): void;
  /** 面が選ばれた。引数は `LEVELS` の添字（`loadLevelAt` にそのまま渡せる）。 */
  onSelect(levelIndex: number): void;
  /** 画面が開いた／閉じた。main が入力の抑止に使う。 */
  onVisibilityChange?(open: boolean): void;
  /**
   * 売り場を開く。**省略するとボタンを作らない。**
   *
   * ## なぜ一覧の側にも置くのか
   * 売り場は盤面の「≡」の中、しかも下までスクロールした先にあった。
   * **二段深く、目立たない。** 審査で「課金の場所が分からない」と
   * 差し戻されたのは、この深さが原因である可能性が高い。
   * 遊ぶ人にとっても、家（ステージ一覧）に入口が無いのは不親切だった。
   */
  onOpenStore?(): void;
  /**
   * 設定を開く。**省略するとボタンを作らない。**
   *
   * 盤面には左上に「≡」があるが、**起動して最初に見えるこの画面には無かった。**
   * 音量も配色も、面を1つ開かないと触れないことになる。
   * 位置は盤面と同じ左上に揃える——**同じ物は同じ場所に**あるべきで、
   * 画面ごとに探し直させない。
   */
  onOpenSettings?(): void;
  /** 売り場を出してよいか。**表示のたびに訊く**（課金の登録は後から済む）。 */
  canOpenStore?(): boolean;
  /** 体験版だけ。続きへ送る App Store の URL。 */
  appStoreUrl?: string;
  /** 体験版の上限。文言に面数を入れるために持つ。 */
  demoLimit?: number;
  /**
   * 自前の「ステージ」ボタンを盤面に置くか。既定は置く。
   *
   * `false` にすると入口を持たない（`open()` を外から呼ぶ前提になる）。
   * 起動時にこの画面から始めるようにしたので、盤面に常駐する入口は
   * 実質「TOP に戻る」ボタンであり、遊んでいる最中の紙面に居座る理由が無い。
   * いまは `ui/settings.ts` の「≡」の中に畳んである。
   */
  withOpenButton?: boolean;
}

export interface StageSelect {
  readonly isOpen: boolean;
  /** 最新の記録で中身を組み直して開く。 */
  open(): void;
  close(): void;
  toggle(): void;
  /** 開いていれば描き直す（今日の一面が届いたときなど）。閉じていれば何もしない。 */
  refresh(): void;
  dispose(): void;
}

export function createStageSelect(
  root: HTMLElement,
  callbacks: StageSelectCallbacks,
): StageSelect {
  injectStyles();

  const withOpenButton = callbacks.withOpenButton !== false;
  const openBtn = el('button', 'btn ef-ss-open', t.stages);
  openBtn.type = 'button';
  openBtn.hidden = !withOpenButton;
  openBtn.inert = !withOpenButton;

  const panel = el('div', 'ef-ss');
  const head = el('div', 'ef-ss-head');
  const title = el('p', 'ef-ss-title', t.stages);
  const sub = el('p', 'ef-ss-sub');
  const award = el('p', 'ef-ss-award', t.completionTitle);
  award.hidden = true;

  // 周回の切り替え。二周目が解放されるまで出さない。
  const loops = el('div', 'ef-ss-loops');
  const loop1Btn = el('button', 'btn ef-ss-loop', t.loop1);
  const loop2Btn = el('button', 'btn ef-ss-loop', t.loop2);
  loop1Btn.type = 'button';
  loop2Btn.type = 'button';
  loops.append(loop1Btn, loop2Btn);
  const loopNote = el(
    'p',
    'ef-ss-loop-note',
    t.loopNote,
  );

  // 売り場の入口。**家に置く。** 盤面の「≡」の奥だけでは見つからない。
  // ## 体験版の案内
  // **一覧の一番上に置く。** 5面通して閉じた面を見たとき、
  // 「なぜ開かないのか」と「どこで続きを遊べるか」が同じ場所に無いと迷う。
  // `<a>` にするのは、ブラウザで新しいタブに開かせたいから
  // （`#ui a` は pointer-events: auto になっている）。
  const demoBox = el('div', 'ef-ss-demo');
  const demoNote = el('p', 'ef-ss-demo-note', t.demoNote(callbacks.demoLimit ?? 0));
  const demoLink = document.createElement('a');
  demoLink.className = 'btn btn--water ef-ss-demo-link';
  demoLink.textContent = t.demoGet;
  demoLink.href = callbacks.appStoreUrl ?? '#';
  demoLink.target = '_blank';
  demoLink.rel = 'noopener';
  demoBox.append(demoNote, demoLink);
  demoBox.hidden = callbacks.appStoreUrl === undefined;

  // ## 今日の一面
  // **いちばん上に置く。** 毎日開く理由そのものなので、本編の一覧に埋もれさせない。
  // 枠の色は体験版の案内と同じ水の縁取り——「いつもと違う、今日だけのもの」の印。
  const dailyBox = el('div', 'ef-ss-daily');
  const dailyHead = el('p', 'ef-ss-daily-title');
  const dailyStatus = el('p', 'ef-ss-daily-note');
  const dailyBtn = el('button', 'btn btn--water ef-ss-daily-play', t.dailyPlay);
  dailyBtn.type = 'button';
  dailyBox.append(dailyHead, dailyStatus, dailyBtn);
  dailyBox.hidden = true;

  const storeBtn = el('button', 'btn ef-ss-store', t.shop);
  storeBtn.type = 'button';
  // 設定の入口。**盤面の「≡」と同じ左上**に置く。
  const setBtn = el('button', 'btn ef-ss-set', '≡');
  setBtn.type = 'button';
  setBtn.setAttribute('aria-label', t.settingsAria);
  head.append(title, sub, award, loops, loopNote);
  if (callbacks.onOpenStore) head.append(storeBtn);

  const mainGroup = el('div', 'ef-ss-group');
  const mainTitle = el('p', 'ef-ss-group-title', t.groupMain);
  const mainGrid = el('div', 'ef-ss-grid');
  mainGroup.append(mainTitle, mainGrid);

  const extraGroup = el('div', 'ef-ss-group');
  const extraTitle = el('p', 'ef-ss-group-title', t.groupExtra1);
  const extraGrid = el('div', 'ef-ss-grid');
  // 文言は render で入れ直す。**解放条件が課金に変わったので固定文にできない**
  // （「本編をすべて通水すると開く」のまま残っていて、存在しない条件を案内していた）。
  const extraNote = el('p', 'ef-ss-locked-note');
  extraGroup.append(extraTitle, extraNote, extraGrid);

  // **エクストラは前半・後半を一つの並びにする**（2026-10-08、ユーザーの指示）。
  // 二段に分けると「其の二」が別の商品に見えるうえ、20面の規模が一目で伝わらない。
  // 解放の順番（前半10面をすべて通すと後半が開く）は変えていない。案内だけ同じ枠に置く。
  const extra2Note = el('p', 'ef-ss-locked-note', t.extra2Note);
  extraGroup.insertBefore(extra2Note, extraGrid);


  // ## これまでの一面
  // **本編・エクストラとは別の棚。** 毎日増えるので、本編に混ぜると「全何面」も
  // 全通水のゴールも動き続けてしまう（2026-10-08 の判断）。
  const archiveGroup = el('div', 'ef-ss-group');
  const archiveTitle = el('p', 'ef-ss-group-title', t.archiveTitle);
  const archiveNote = el('p', 'ef-ss-locked-note', t.archiveLocked);
  const archiveGrid = el('div', 'ef-ss-grid');
  archiveGroup.append(archiveTitle, archiveNote, archiveGrid);
  archiveGroup.hidden = true;

  // ## 先の一面（エクストラを買った人だけ）
  const upcomingGroup = el('div', 'ef-ss-group');
  const upcomingTitle = el('p', 'ef-ss-group-title', t.upcomingTitle);
  const upcomingGrid = el('div', 'ef-ss-grid');
  upcomingGroup.append(upcomingTitle, upcomingGrid);
  upcomingGroup.hidden = true;

  const foot = el('div', 'ef-ss-foot');
  const closeBtn = el('button', 'btn ef-ss-close', t.back);
  closeBtn.type = 'button';
  foot.append(closeBtn);

  if (callbacks.onOpenSettings) panel.append(setBtn);
  panel.append(head, demoBox, dailyBox, mainGroup, extraGroup, upcomingGroup, archiveGroup, foot);
  if (withOpenButton) root.append(openBtn, panel);
  else root.append(panel);

  let open = false;

  const bound: Array<{ el: EventTarget; type: string; fn: EventListener }> = [];
  const on = (target: EventTarget, type: string, fn: EventListener): void => {
    target.addEventListener(type, fn);
    bound.push({ el: target, type, fn });
  };

  /**
   * 1枚ぶんのカードを作る。未解放なら押せない伏せ札にする。
   *
   * ## エクストラだけは名前を見せる
   * 本編を伏せるのは**先の面を先取りさせない**ため。
   * エクストラを伏せるのは、そうではなく**売り物を隠している**だけだった。
   * 「？」は好奇心を生まない。名前（逆しまの滝、涸れ井戸…）が見えて
   * 初めて「何があるのか」を想像でき、買う理由になる。
   * 盤面は伏せたままなので、解き味は先取りされない。
   */
  function makeCard(entry: StageEntry, revealName = false): HTMLButtonElement {
    const card = el('button', 'ef-ss-card');
    card.type = 'button';
    const locked = !entry.unlocked;
    card.disabled = locked;
    if (entry.cleared) card.classList.add('is-cleared');
    if (entry.isFreeTrial) card.classList.add('is-trial');

    const show = !locked || revealName;
    card.append(
      el('span', 'ef-ss-no', show ? stageNumber(entry.number) : '？'),
      el('span', 'ef-ss-name', show ? levelName(entry.id, entry.name) : ''),
      el(
        'span',
        'ef-ss-time',
        entry.isFreeTrial
          ? t.freeTrial
          : locked
            ? ''
            : entry.bestTimeMs === null
              ? '—'
              : formatTime(entry.bestTimeMs),
      ),
    );
    card.setAttribute(
      'aria-label',
      locked
        ? show
          ? t.notPurchased(String(entry.number), levelName(entry.id, entry.name))
          : t.lockedStage
        : `${entry.number} ${levelName(entry.id, entry.name)}`,
    );
    if (!locked) on(card, 'click', () => callbacks.onSelect(entry.levelIndex));
    return card;
  }

  function render(): void {
    const { levels, records, unlockAll, loop, demoLimit, daily } = callbacks.getState();
    dailyBox.hidden = daily === undefined;
    if (daily !== undefined) {
      dailyHead.textContent = `${t.dailyTitle}　${daily.dateLabel}`;
      dailyStatus.textContent =
        daily.status === 'loading' ? t.dailyLoading
          : daily.status === 'offline' ? t.dailyOffline
            : daily.bestTimeMs !== null ? t.dailyCleared(formatTime(daily.bestTimeMs)) : '';
      dailyStatus.hidden = dailyStatus.textContent === '';
      dailyBtn.hidden = daily.status !== 'ready';
    }
    archiveGroup.hidden = daily === undefined || daily.archive.length === 0;
    if (daily !== undefined) {
      archiveNote.hidden = !daily.archiveHasLocked;
      archiveGrid.replaceChildren(...daily.archive.map((d) => {
        const card = el('button', 'ef-ss-card');
        card.type = 'button';
        card.disabled = !d.open;
        if (d.bestTimeMs !== null) card.classList.add('is-cleared');
        card.append(
          el('span', 'ef-ss-name', d.label),
          el('span', 'ef-ss-time', !d.open ? '' : d.bestTimeMs === null ? '—' : formatTime(d.bestTimeMs)),
        );
        card.setAttribute('aria-label', `${t.archiveTitle} ${d.label}`);
        if (d.open) on(card, 'click', () => callbacks.onPlayArchive?.(d.date));
        return card;
      }));
    }
    upcomingGroup.hidden = daily === undefined || daily.upcoming.length === 0;
    if (daily !== undefined) {
      upcomingGrid.replaceChildren(...daily.upcoming.map((d) => {
        const card = el('button', 'ef-ss-card');
        card.type = 'button';
        if (d.bestTimeMs !== null) card.classList.add('is-cleared');
        card.append(
          el('span', 'ef-ss-name', d.label),
          el('span', 'ef-ss-time', d.bestTimeMs === null ? '—' : formatTime(d.bestTimeMs)),
        );
        card.setAttribute('aria-label', `${t.upcomingTitle} ${d.label}`);
        on(card, 'click', () => callbacks.onPlayArchive?.(d.date));
        return card;
      }));
    }
    const list = buildStageList(levels, records, { unlockAll, loop, demoLimit });

    // 二周目は本編を全通水した人にだけ出す。
    const loop2Open = records.progress.unlockedLoop2 || unlockAll === true;
    // **描くたびに訊く。** 課金の登録は起動より後に済むので、
    // 一度きりの判定にすると永久に出ない（設定画面で同じ事故があった）。
    storeBtn.hidden = callbacks.canOpenStore?.() === false;
    loops.hidden = !loop2Open;
    loopNote.hidden = !loop2Open || loop < 2;
    loop1Btn.classList.toggle('is-on', loop < 2);
    loop2Btn.classList.toggle('is-on', loop >= 2);
    const progress = mainProgress(levels, records);
    const total = mainTotalBestTime(levels, records);

    const parts = [t.progress(progress.cleared, progress.total)];
    if (total !== null) {
      parts.push(t.totalTime(formatTime(total)), t.rank[rankOf(total, progress.total)]);
    }
    sub.textContent = parts.join('　—　');

    // 一度得たら消えない。面が増えても、記録が新しい面で埋まらなくても出し続ける
    // （hasCompletionTitle は刻まれた日時だけを見る）。
    award.hidden = !hasCompletionTitle(records);

    mainGrid.replaceChildren(
      ...list.filter((s) => s.category === 'main').map((e) => makeCard(e)),
    );

    const extras = list.filter((s) => s.category === 'extra' && !s.isExtraTier2);
    extraGroup.hidden = extras.length === 0 && list.every((s) => !s.isExtraTier2);
    const owned = records.progress.extrasOwned || records.progress.unlockedExtra;
    // 試遊が開いているなら、まずそれを勧める。閉じているなら買い方を書く。
    const trialOpen = extras.some((s2) => s2.isFreeTrial);
    // 体験版では購入の案内が嘘になる（売り場が無い）。上の案内に一本化して、ここは黙る。
    extraNote.hidden = demoLimit !== undefined;
    extra2Note.hidden = demoLimit !== undefined;
    extraNote.textContent = trialOpen
      ? t.extraLockedFree
      : t.extraLocked;
    extraNote.hidden = owned || unlockAll === true;
    const extras2 = list.filter((s) => s.isExtraTier2);
    extra2Note.textContent = owned
      ? t.extra2Locked
      : t.extra2LockedUnowned;
    // 未購入のときは上の購入案内で足りる。二つ並べると同じことを二度言う。
    extra2Note.hidden =
      extras2.length === 0 ||
      !owned ||
      extras2.some((s) => s.unlocked) ||
      unlockAll === true ||
      demoLimit !== undefined;
    extraGrid.replaceChildren(...[...extras, ...extras2].map((e) => makeCard(e, true)));
  }

  function setOpen(next: boolean): void {
    if (open === next) return;
    open = next;
    panel.classList.toggle('is-on', open);
    panel.inert = !open;
    // 入口を持たない構成では、隠したままにしておく。
    openBtn.inert = open || !withOpenButton;
    openBtn.hidden = open || !withOpenButton;
    callbacks.onVisibilityChange?.(open);
    // preventScroll が無いと、焦点を当てた拍子に「もどる」が見える位置まで
    // 一覧が送られ、見出しと進捗が画面外に出る（実機で確認）。
    // 焦点そのものは Esc とキーボード操作のために要るので、送りだけ止める。
    if (open) closeBtn.focus({ preventScroll: true });
  }

  on(loop1Btn, 'click', () => {
    callbacks.onLoopChange(1);
    render();
  });
  on(loop2Btn, 'click', () => {
    callbacks.onLoopChange(2);
    render();
  });

  on(openBtn, 'click', () => {
    render();
    setOpen(true);
  });
  on(closeBtn, 'click', () => setOpen(false));
  on(dailyBtn, 'click', () => callbacks.onPlayDaily?.());
  if (callbacks.onOpenSettings) {
    const onOpenSettings = callbacks.onOpenSettings.bind(callbacks);
    on(setBtn, 'click', () => onOpenSettings());
  }
  if (callbacks.onOpenStore) {
    const onOpenStore = callbacks.onOpenStore.bind(callbacks);
    on(storeBtn, 'click', () => onOpenStore());
  }
  on(window, 'keydown', (event) => {
    if (!open) return;
    if ((event as KeyboardEvent).key !== 'Escape') return;
    event.preventDefault();
    setOpen(false);
  });

  panel.inert = true;

  return {
    get isOpen(): boolean {
      return open;
    },
    open() {
      render();
      setOpen(true);
    },
    close() {
      setOpen(false);
    },
    refresh() {
      if (open) render();
    },
    toggle() {
      if (open) {
        setOpen(false);
        return;
      }
      render();
      setOpen(true);
    },
    dispose() {
      for (const b of bound) b.el.removeEventListener(b.type, b.fn);
      bound.length = 0;
      openBtn.remove();
      panel.remove();
      releaseStyles();
    },
  };
}
